import { MultiPropertyProvider, Property, PropertyChangedResult, PropertyClass, PropertyValue, groupProperties } from '@easterngraphics/wcf/modules/core/prop';
import { isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';
import { HtmlUtils } from '../../utils';
import { ProgressUI } from '../progress';
import './index.css';

interface ScrollAnchor {
    propertyName: string;
    propertyValue?: string;
    offsetWithinContainer: number;
}

interface ScrollSnapshot {
    scrollTop: number;
    interactionAnchor: ScrollAnchor | null;
    topVisibleAnchor: ScrollAnchor | null;
}
/**
 * For configuring articles.
 * Shows article properties and their options to the user. So he can change properties of an article.
 */
export class PropertyEditorUI {
    private propertyProvider: MultiPropertyProvider;
    private htmlContainer: HTMLElement;
    private readonly propertyClassElement: HTMLDivElement;
    private pendingScrollSnapshot: ScrollSnapshot | null = null;

    constructor(htmlContainer: HTMLElement, propertyProvider: MultiPropertyProvider) {
        this.propertyProvider = propertyProvider;
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        this.propertyProvider.eventPropertiesChanged.addListener(this.onPropertiesChanged.bind(this));
        this.htmlContainer = htmlContainer;
        this.propertyClassElement = this.createPropertyClass();
        this.htmlContainer.appendChild(this.propertyClassElement);
    }

    private async onPropertiesChanged(result?: PropertyChangedResult): Promise<void> {
        if (result !== PropertyChangedResult.Nothing) {
            await this.updatePropertyEditor();
        }
    }

    private async updatePropertyEditor(): Promise<void> {
        const scrollSnapshot = this.pendingScrollSnapshot ?? this.captureScrollSnapshot();
        this.pendingScrollSnapshot = null;
        HtmlUtils.removeAllChildren(this.propertyClassElement);
        try {
            const propertyClasses: Array<PropertyClass> = await this.propertyProvider.getPropertyClasses();
            const properties: Array<Property> = await this.propertyProvider.getProperties();
            const visibleProperties = properties.filter((property) => property.visible && property.editable);
            const propertyGroups = groupProperties(visibleProperties, propertyClasses, { defaultClassName: 'Properties' });

            for (const group of propertyGroups) {
                if (group.properties.length === 0) {
                    continue;
                }

                for (const property of group.properties) {
                    this.propertyClassElement.appendChild(await this.createProperty(property));
                }
            }
        } catch (error) {
            console.error('failed to get properties', error);
            this.propertyClassElement.innerText = 'Migration of article might be required.';
        } finally {
            this.restoreScrollSnapshot(scrollSnapshot);
            document.dispatchEvent(new Event('property-editor:updated'));
        }
    }

    private createPropertyClass(): HTMLDivElement {
        const propClassHtml: HTMLDivElement = document.createElement('div');
        propClassHtml.className = 'property-class';
        return propClassHtml;
    }

    private async createProperty(property: Property): Promise<HTMLElement> {
        const propertyHtml: HTMLDivElement = document.createElement('div');
        propertyHtml.className = `property ${this.slugify(property.getName())}`;
        propertyHtml.dataset.propertyName = property.getName();
        propertyHtml.dataset.editable = property.editable ? 'true' : 'false';
        propertyHtml.dataset.visible = property.visible ? 'true' : 'false';
        propertyHtml.dataset.choiceList = property.choiceList ? 'true' : 'false';

        const title = document.createElement('div');
        title.className = 'property-title';
        title.innerText = property.getName();
        propertyHtml.appendChild(title);

        if (property.choiceList) {
            const propertyChoices: Array<PropertyValue> | null = await property.getChoices();
            if (propertyChoices != null) {
                propertyChoices.forEach((propertyValue) => {
                    propertyHtml.appendChild(this.createPropertyChoice(property, propertyValue));
                });
            }
            return propertyHtml;
        }

        const propertyValue: PropertyValue | null = property.getValue();
        const valueElement = document.createElement('div');
        valueElement.className = 'property-value';
        valueElement.dataset.propertyName = property.getName();
        valueElement.dataset.propertyValue = this.serializePropertyValue(propertyValue?.value);
        valueElement.innerText = propertyValue?.text ?? '';
        propertyHtml.appendChild(valueElement);
        propertyHtml.onclick = () => {
            void this.onDirectPropertyClick(property, propertyHtml);
        };

        return propertyHtml;
    }

    private async onDirectPropertyClick(property: Property, propertyElement: HTMLElement): Promise<void> {
        ProgressUI.beginLoading();
        try {
            const userInput: string | null = prompt(property.getName(), String(property.getValue()?.value ?? ''));
            if (userInput != null && userInput !== String(property.getValue()?.value ?? '')) {
                this.pendingScrollSnapshot = this.captureScrollSnapshot(propertyElement);
                await property.setValue(userInput);
            }
        } finally {
            ProgressUI.endLoading();
        }
    }

    private createPropertyChoice(property: Property, propertyValue: PropertyValue): HTMLElement {
        const propertyChoice: HTMLDivElement = document.createElement('div');
        propertyChoice.className = 'property-choice' + (property.getValue()?.value === propertyValue.value ? ' selected' : '');
        propertyChoice.dataset.propertyName = property.getName();
        propertyChoice.dataset.propertyValue = this.serializePropertyValue(propertyValue.value);
        propertyChoice.dataset.selectable = propertyValue.selectable ? 'true' : 'false';

        propertyChoice.onclick = async (event) => {
            if (!propertyValue.selectable) {
                event.stopPropagation();
                return;
            }
            if (property.getValue()?.value === propertyValue.value) {
                return;
            }
            ProgressUI.beginLoading();
            try {
                this.pendingScrollSnapshot = this.captureScrollSnapshot(propertyChoice);
                await property.setValue(propertyValue.value);
            } finally {
                ProgressUI.endLoading();
            }
        };

        if (!isNullOrEmpty(propertyValue.largeIcon)) {
            const icon: HTMLImageElement = document.createElement('img');
            icon.src = propertyValue.largeIcon;
            propertyChoice.appendChild(icon);
        }

        if (!isNullOrEmpty(propertyValue.text)) {
            const label: HTMLDivElement = document.createElement('div');
            label.innerText = propertyValue.text;
            propertyChoice.appendChild(label);
        }

        return propertyChoice;
    }

    private slugify(value: string): string {
        return value.replace(/[\W_]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
    }

    private captureScrollSnapshot(interactionElement?: HTMLElement | null): ScrollSnapshot | null {
        const container = this.propertyClassElement;
        const interactionAnchor = this.createScrollAnchor(interactionElement ?? null);
        const topVisibleAnchor = this.createScrollAnchor(this.findTopVisibleProperty());

        if (interactionAnchor == null && topVisibleAnchor == null && container.scrollTop === 0) {
            return null;
        }

        return {
            scrollTop: container.scrollTop,
            interactionAnchor,
            topVisibleAnchor,
        };
    }

    private restoreScrollSnapshot(snapshot: ScrollSnapshot | null): void {
        if (snapshot == null) {
            return;
        }

        const interactionAnchorElement = this.findAnchorElement(snapshot.interactionAnchor);
        if (interactionAnchorElement != null && snapshot.interactionAnchor != null) {
            this.restoreAnchorOffset(interactionAnchorElement, snapshot.interactionAnchor);
            return;
        }

        const topAnchorElement = this.findAnchorElement(snapshot.topVisibleAnchor);
        if (topAnchorElement != null && snapshot.topVisibleAnchor != null) {
            this.restoreAnchorOffset(topAnchorElement, snapshot.topVisibleAnchor);
            return;
        }

        this.propertyClassElement.scrollTop = this.clampScrollTop(snapshot.scrollTop);
    }

    private restoreAnchorOffset(anchorElement: HTMLElement, anchor: ScrollAnchor): void {
        const containerRect = this.propertyClassElement.getBoundingClientRect();
        const anchorRect = anchorElement.getBoundingClientRect();
        const currentOffset = anchorRect.top - containerRect.top;
        const nextScrollTop = this.propertyClassElement.scrollTop + currentOffset - anchor.offsetWithinContainer;
        this.propertyClassElement.scrollTop = this.clampScrollTop(nextScrollTop);
    }

    private clampScrollTop(scrollTop: number): number {
        const maxScrollTop = Math.max(this.propertyClassElement.scrollHeight - this.propertyClassElement.clientHeight, 0);
        return Math.max(0, Math.min(scrollTop, maxScrollTop));
    }

    private createScrollAnchor(anchorElement: HTMLElement | null): ScrollAnchor | null {
        if (anchorElement == null) {
            return null;
        }

        const propertyElement = anchorElement.classList.contains('property')
            ? anchorElement
            : anchorElement.closest<HTMLElement>('.property');
        const propertyName = propertyElement?.dataset.propertyName;
        if (propertyName == null) {
            return null;
        }

        const containerRect = this.propertyClassElement.getBoundingClientRect();
        const anchorRect = anchorElement.getBoundingClientRect();

        return {
            propertyName,
            propertyValue: anchorElement.dataset.propertyValue,
            offsetWithinContainer: anchorRect.top - containerRect.top,
        };
    }

    private findTopVisibleProperty(): HTMLElement | null {
        const propertyElements = Array.from(this.propertyClassElement.querySelectorAll<HTMLElement>('.property'));
        if (propertyElements.length === 0) {
            return null;
        }

        const containerTop = this.propertyClassElement.getBoundingClientRect().top;
        const firstVisibleProperty = propertyElements.find((propertyElement) => propertyElement.getBoundingClientRect().bottom > containerTop);
        return firstVisibleProperty ?? propertyElements[propertyElements.length - 1];
    }

    private findAnchorElement(anchor: ScrollAnchor | null): HTMLElement | null {
        if (anchor == null) {
            return null;
        }

        if (anchor.propertyValue != null) {
            const anchorElement = Array.from(this.propertyClassElement.querySelectorAll<HTMLElement>('.property-choice, .property-value'))
                .find((element) => element.dataset.propertyName === anchor.propertyName && element.dataset.propertyValue === anchor.propertyValue);
            if (anchorElement != null) {
                return anchorElement;
            }
        }

        return Array.from(this.propertyClassElement.querySelectorAll<HTMLElement>('.property'))
            .find((element) => element.dataset.propertyName === anchor.propertyName) ?? null;
    }

    private serializePropertyValue(value: string | number | boolean | null | undefined): string {
        return String(value ?? '');
    }
}
